// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { clearAuthTokenCache } from "../utils/authToken";
import { auth } from "../firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signOut
} from "firebase/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState(null);

  // Gathers local storage progress to upload to Cloudflare D1
  const getLocalProgress = () => {
    const historyRaw = localStorage.getItem("ebenezer_reading_history");
    const badgesRaw = localStorage.getItem("ebenezer_unlocked_badges");
    
    // Gather all bookmark_* keys
    const bookmarks = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("bookmark_")) {
        const bookId = key.replace("bookmark_", "");
        bookmarks[bookId] = parseInt(localStorage.getItem(key), 10) || 0;
      }
    }

    // Gather all reader_annotations_* keys (notes/highlights)
    const notes = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("reader_annotations_")) {
        const bookId = key.replace("reader_annotations_", "");
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          if (Array.isArray(parsed)) {
            notes[bookId] = parsed;
          }
        } catch (e) {
          console.warn("[Auth] Failed to parse local annotations for book:", bookId, e);
        }
      }
    }

    return {
      streaks: {
        currentStreak: parseInt(localStorage.getItem("ebenezer_current_streak"), 10) || 0,
        longestStreak: parseInt(localStorage.getItem("ebenezer_longest_streak"), 10) || 0,
      },
      badges: badgesRaw ? JSON.parse(badgesRaw) : [],
      bookmarks,
      reading_history: historyRaw ? JSON.parse(historyRaw) : {},
      notes,
      last_updated: parseInt(localStorage.getItem("ebenezer_progress_last_updated"), 10) || 0,
    };
  };

  // Applies fetched cloud D1 progress back down to local storage
  const applyCloudProgress = (data) => {
    if (!data) return;
    
    if (data.reading_history) {
      localStorage.setItem("ebenezer_reading_history", JSON.stringify(data.reading_history));
    }
    if (data.badges) {
      localStorage.setItem("ebenezer_unlocked_badges", JSON.stringify(data.badges));
    }
    if (data.streaks) {
      if (data.streaks.currentStreak !== undefined) {
        localStorage.setItem("ebenezer_current_streak", String(data.streaks.currentStreak));
      }
      if (data.streaks.longestStreak !== undefined) {
        localStorage.setItem("ebenezer_longest_streak", String(data.streaks.longestStreak));
      }
    }
    if (data.bookmarks) {
      Object.entries(data.bookmarks).forEach(([bookId, pageNum]) => {
        localStorage.setItem(`bookmark_${bookId}`, String(pageNum));
      });
    }
    if (data.notes) {
      Object.entries(data.notes).forEach(([bookId, annotationsList]) => {
        localStorage.setItem(`reader_annotations_${bookId}`, JSON.stringify(annotationsList));
      });
    }
    if (data.last_updated) {
      localStorage.setItem("ebenezer_progress_last_updated", String(data.last_updated));
    }
  };

  // Clear reading-related keys upon logout (preserves UI settings like theme)
  const clearLocalReadingData = () => {
    localStorage.removeItem("ebenezer_reading_history");
    localStorage.removeItem("ebenezer_unlocked_badges");
    localStorage.removeItem("ebenezer_current_streak");
    localStorage.removeItem("ebenezer_longest_streak");
    localStorage.removeItem("ebenezer_daily_goal");
    localStorage.removeItem("ebenezer_progress_last_updated");
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith("bookmark_") || key.startsWith("user_rating_") || key.startsWith("reader_annotations_"))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  // Merges local and cloud progress objects intelligently
  const mergeProgress = (local, cloud) => {
    // 1. Merged bookmarks
    const bookmarks = { ...(cloud.bookmarks || {}) };
    if (local.bookmarks) {
      Object.entries(local.bookmarks).forEach(([bookId, localPage]) => {
        const cloudPage = bookmarks[bookId] || 0;
        bookmarks[bookId] = Math.max(localPage, cloudPage);
      });
    }

    // 2. Merged badges
    const badgesSet = new Set([...(cloud.badges || []), ...(local.badges || [])]);
    const badges = Array.from(badgesSet);

    // 3. Merged streaks
    const streaks = {
      currentStreak: Math.max(local.streaks?.currentStreak || 0, cloud.streaks?.currentStreak || 0),
      longestStreak: Math.max(local.streaks?.longestStreak || 0, cloud.streaks?.longestStreak || 0),
    };

    // 4. Merged reading history
    const reading_history = { ...(cloud.reading_history || {}) };
    if (local.reading_history) {
      Object.entries(local.reading_history).forEach(([dateStr, localDay]) => {
        const cloudDay = reading_history[dateStr] || { pages: {}, timestamps: [] };
        const pages = { ...(cloudDay.pages || {}) };

        if (localDay.pages) {
          Object.entries(localDay.pages).forEach(([bookId, localPagesList]) => {
            const cloudPagesList = pages[bookId] || [];
            pages[bookId] = Array.from(new Set([...cloudPagesList, ...localPagesList]));
          });
        }

        const combinedTimestamps = [...(cloudDay.timestamps || []), ...(localDay.timestamps || [])];
        const seenTimestampKeys = new Set();
        const timestamps = combinedTimestamps.filter((t) => {
          const key = `${t.bookId}_${t.page}_${t.time}`;
          if (seenTimestampKeys.has(key)) return false;
          seenTimestampKeys.add(key);
          return true;
        });

        reading_history[dateStr] = { pages, timestamps };
      });
    }

    // 5. Merged notes
    const notes = { ...(cloud.notes || {}) };
    if (local.notes) {
      Object.entries(local.notes).forEach(([bookId, localList]) => {
        const cloudList = notes[bookId] || [];
        const combinedMap = new Map();
        cloudList.forEach((item) => combinedMap.set(item.id, item));
        localList.forEach((item) => {
          const existing = combinedMap.get(item.id);
          if (!existing || new Date(item.createdAt || 0) > new Date(existing.createdAt || 0)) {
            combinedMap.set(item.id, item);
          }
        });
        notes[bookId] = Array.from(combinedMap.values()).sort(
          (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        );
      });
    }

    return {
      streaks,
      badges,
      bookmarks,
      reading_history,
      notes,
      last_updated: Date.now(),
    };
  };

  // Check admin status from D1 — accepts a pre-fetched token to avoid redundant getIdToken() calls
  const checkAdminStatus = async (currentUser, token) => {
    if (!currentUser) {
      setIsAdmin(false);
      setAdminRole(null);
      return;
    }
    try {
      const idToken = token || await currentUser.getIdToken();
      const res = await fetch("/api/auth/check-admin", {
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });
      if (!res.ok) {
        setIsAdmin(false);
        setAdminRole(null);
        return;
      }
      const result = await res.json();
      setIsAdmin(result.isAdmin || false);
      setAdminRole(result.role || null);
    } catch (err) {
      console.error("[Auth] Check admin status failed:", err);
      setIsAdmin(false);
      setAdminRole(null);
    }
  };

  // Trigger POST sync to D1 — accepts a pre-fetched token to avoid redundant getIdToken() calls
  const syncToCloud = async (currentUser = user, token = null) => {
    if (!currentUser) return;
    try {
      setSyncing(true);
      const idToken = token || await currentUser.getIdToken();
      const payload = getLocalProgress();
      
      const res = await fetch("/api/auth/sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify(payload),
        keepalive: true
      });
      if (!res.ok) throw new Error("Cloud sync upload failed");
    } catch (e) {
      console.error("[Auth] Sync upload failed:", e);
    } finally {
      setSyncing(false);
    }
  };

  // Trigger GET fetch from D1 and sync local storage — accepts a pre-fetched token to avoid redundant getIdToken() calls
  const syncFromCloud = async (currentUser, token) => {
    if (!currentUser) return;
    try {
      setSyncing(true);
      const idToken = token || await currentUser.getIdToken();
      const res = await fetch("/api/auth/sync", {
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });
      if (!res.ok) throw new Error("Cloud sync fetch failed");
      const result = await res.json();
      
      if (result.success && result.data) {
        const cloudData = result.data;
        const local = getLocalProgress();

        const cloudTime = cloudData.last_updated || 0;
        const localTime = local.last_updated || 0;

        const hasCloudData = (cloudData.badges && cloudData.badges.length > 0) || 
                             (cloudData.bookmarks && Object.keys(cloudData.bookmarks).length > 0);
        const hasLocalData = (local.badges && local.badges.length > 0) || 
                             (local.bookmarks && Object.keys(local.bookmarks).length > 0);

        // Conflict Resolution Strategy — reuse same token to avoid extra getIdToken() calls
        if (hasLocalData && (!hasCloudData || localTime > cloudTime)) {
          if (hasCloudData) {
            const merged = mergeProgress(local, cloudData);
            applyCloudProgress(merged);
            await syncToCloud(currentUser, idToken);
          } else {
            await syncToCloud(currentUser, idToken);
          }
        } else if (hasCloudData && cloudTime > localTime) {
          applyCloudProgress(cloudData);
        }
      }
    } catch (e) {
      console.error("[Auth] Sync download failed:", e);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      
      if (currentUser) {
        // Fetch token ONCE and share it across all startup calls — avoids quota errors
        let token = null;
        try {
          token = await currentUser.getIdToken();
        } catch (err) {
          console.error("[Auth] Failed to get ID token:", err);
        }
        await checkAdminStatus(currentUser, token);
        await syncFromCloud(currentUser, token);
      } else {
        setIsAdmin(false);
        setAdminRole(null);
      }
    });
    return unsubscribe;
  }, []);

  const loginWithEmail = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signupWithEmail = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const loginWithFacebook = () => {
    const provider = new FacebookAuthProvider();
    return signInWithPopup(auth, provider);
  };

  const logout = async () => {
    try {
      await syncToCloud();
    } catch (e) {}
    clearAuthTokenCache();
    await signOut(auth);
    clearLocalReadingData();
    setIsAdmin(false);
    setAdminRole(null);
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        syncing,
        isAdmin,
        adminRole,
        loginWithEmail,
        signupWithEmail,
        loginWithGoogle,
        loginWithFacebook,
        logout,
        syncToCloud
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
